import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { Mail, ArrowLeft, Loader2, CheckCircle } from "lucide-react";

const GBA_LOGO = "https://customer-assets.emergentagent.com/job_prospect-tracker-24/artifacts/aek653mu_GBA%20LOGO%20PNG.png";

const API = process.env.REACT_APP_BACKEND_URL;

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email });
      setSubmitted(true);
      toast.success("Reset link sent! Check your email.");
    } catch (error) {
      // Still show success to prevent email enumeration
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0B1120] flex flex-col items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-8 animate-fade-in">
            <img src={GBA_LOGO} alt="GBA" className="h-20 mx-auto mb-6" />
          </div>

          <div className="bg-[#1E293B] rounded-2xl p-8 border border-slate-700 shadow-xl animate-slide-up text-center">
            <CheckCircle className="w-16 h-16 text-gba-green mx-auto mb-4" />
            <h2 className="font-heading text-2xl font-semibold text-white mb-4">
              Check Your Email
            </h2>
            <p className="text-slate-400 mb-6">
              If an account exists for <span className="text-white">{email}</span>, 
              we've sent a password reset link. Please check your inbox and spam folder.
            </p>
            <Link to="/login">
              <Button className="w-full bg-gba-green hover:bg-gba-green-hover">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1120] flex flex-col items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo and Header */}
        <div className="text-center mb-8 animate-fade-in">
          <img src={GBA_LOGO} alt="GBA" className="h-20 mx-auto mb-6" />
          <h1 className="font-heading text-3xl font-bold text-white tracking-tight">
            Forgot Password?
          </h1>
          <p className="text-slate-400 mt-2">
            Enter your email and we'll send you a reset link
          </p>
        </div>

        {/* Form */}
        <div className="bg-[#1E293B] rounded-2xl p-8 border border-slate-700 shadow-xl animate-slide-up">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10 bg-[#0F172A] border-slate-700 text-white placeholder:text-slate-600"
                  data-testid="forgot-email-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-gba-green hover:bg-gba-green-hover text-white font-bold py-6 text-lg uppercase tracking-wide shadow-glow"
              data-testid="forgot-submit-button"
            >
              {loading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                "Send Reset Link"
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Link
              to="/login"
              className="text-slate-400 hover:text-gba-green inline-flex items-center"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
